/**
 * Calendar Screen Handlers Hook
 *
 * Extracts all event handlers from calendar.tsx.
 * Handles date selection, calendar loading, permissions, event CRUD, and modal management.
 */

import { useCallback } from 'react';
import { Platform } from 'react-native';
import type { CalendarScreenManager } from '@/lib/calendar/CalendarScreenManager';
import type { CalendarPreferencesManager } from '@/lib/managers/CalendarPreferencesManager';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import { RecurringEventUpdateDialog } from '@/lib/calendar/RecurringEventUpdateDialog';
import type { CalendarEvent, CreateCalendarEventInput, UpdateCalendarEventInput } from '@/types/events';
import { CalendarEventFormatter } from '@/lib/calendar/CalendarEventFormatter';
import { Toast } from '@/components/ui/molecules/toast';
import { AttendeeResolver } from '@/lib/calendar/AttendeeResolver';
import type { EventWindow } from './use-calendar-screen-state';

type UseCalendarScreenHandlersProps = {
  // Managers
  manager: CalendarScreenManager;
  preferencesManager: CalendarPreferencesManager | null;

  // Refs
  calendarSelectionTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;

  // State setters
  setSelectedDate: (date: string) => void;
  setDeviceCalendars: (calendars: DeviceCalendar[]) => void;
  setSelectedCalendarIds: (ids: string[]) => void;
  setPreferences: (preferences: any) => void;
  setHasPermission: (permission: boolean | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsRequestingPermission: (requesting: boolean) => void;
  setError: (error: string | null) => void;
  setEventModalMode: (mode: 'create' | 'edit') => void;
  setSelectedEvent: (event: CalendarEvent | null) => void;
  setShowEventModal: (show: boolean) => void;
  setIsSubmittingEvent: (submitting: boolean) => void;
  setIsRefreshing: (refreshing: boolean) => void;
  setDisplayedMonth?: (date: Date) => void;
  incrementCalendarKey?: () => void;

  // State values
  selectedCalendarIds: string[];
  deviceCalendars: DeviceCalendar[];
  selectedEvent: CalendarEvent | null;
  eventModalMode: 'create' | 'edit';
  userEmail?: string;

  // Functions
  loadEventsFromDevice: (options?: { forceRefresh?: boolean; targetDate?: string; silent?: boolean }) => Promise<void>;
};

export function useCalendarScreenHandlers({
  manager,
  preferencesManager,
  calendarSelectionTimeoutRef,
  setSelectedDate,
  setDeviceCalendars,
  setSelectedCalendarIds,
  setPreferences,
  setHasPermission,
  setIsLoading,
  setIsRequestingPermission,
  setError,
  setEventModalMode,
  setSelectedEvent,
  setShowEventModal,
  setIsSubmittingEvent,
  setIsRefreshing,
  setDisplayedMonth,
  incrementCalendarKey,
  selectedCalendarIds,
  deviceCalendars,
  selectedEvent,
  eventModalMode,
  userEmail,
  loadEventsFromDevice,
}: UseCalendarScreenHandlersProps) {
  /**
   * Auto-detect calendars that belong to the user
   */
  const autoDetectSelfCalendars = useCallback(
    (calendars: DeviceCalendar[]): string[] => {
      const selfCalendarIds: string[] = [];

      calendars.forEach((calendar) => {
        // Primary calendar is always self
        if (calendar.isPrimary) {
          selfCalendarIds.push(calendar.id);
          return;
        }

        // Calendar owned by user email
        if (
          userEmail &&
          calendar.ownerAccount?.toLowerCase() === userEmail.toLowerCase()
        ) {
          selfCalendarIds.push(calendar.id);
          return;
        }
      });

      return selfCalendarIds;
    },
    [userEmail],
  );

  /**
   * Handle pressing the "Today" button
   */
  const handleTodayPress = useCallback(() => {
    const today = CalendarEventFormatter.getTodayDateString();
    setSelectedDate(today);

    // Sync displayedMonth to today's month
    if (setDisplayedMonth) {
      const [year, month, day] = today.split('-').map(Number);
      setDisplayedMonth(new Date(year, month - 1, day));
    }

    // Force Calendar component to remount with new current date
    if (incrementCalendarKey) {
      incrementCalendarKey();
    }

    // Force refresh events for today to ensure the event list is populated
    loadEventsFromDevice({ targetDate: today, forceRefresh: true });
  }, [setSelectedDate, setDisplayedMonth, incrementCalendarKey, loadEventsFromDevice]);

  /**
   * Handle date change from calendar picker
   */
  const handleDateChanged = useCallback((date: string) => {
    setSelectedDate(date);
  }, [setSelectedDate]);

  /**
   * Handle date selection from calendar
   */
  const handleDateSelect = useCallback((dateString: string) => {
    setSelectedDate(dateString);
  }, [setSelectedDate]);

  /**
   * Load device calendars and preferences
   */
  const loadCalendars = useCallback(async () => {
    try {
      const calendars = await manager.loadDeviceCalendars();
      setDeviceCalendars(calendars);

      // Wait for preferences manager to be ready
      if (!preferencesManager) {
        // Use all calendars if preferences not available yet
        const selection = await manager.loadCalendarSelection(calendars);
        setSelectedCalendarIds(selection);
        return;
      }

      // Load preferences
      const storage = await preferencesManager.loadPreferences();

      // Filter visible calendars
      const visibleCalendars = preferencesManager.filterVisibleCalendars(
        calendars,
        storage.preferences,
      );

      // Load saved selection or auto-select from visible calendars
      const selection = await manager.loadCalendarSelection(visibleCalendars);
      setSelectedCalendarIds(selection);
    } catch (err) {
      console.error('[calendar] Failed to load calendars:', err);
    }
  }, [manager, preferencesManager, setDeviceCalendars, setSelectedCalendarIds]);

  /**
   * Check calendar permission status
   */
  const checkCalendarPermission = useCallback(async () => {
    if (!manager.canAccessCalendar()) {
      setHasPermission(false);
      setIsLoading(false);
      return;
    }

    const status = await manager.checkPermissionStatus();
    setHasPermission(status === 'granted');

    if (status === 'granted') {
      await loadCalendars();
    } else {
      setIsLoading(false);
    }
  }, [manager, loadCalendars, setHasPermission, setIsLoading]);

  /**
   * Request calendar permissions
   */
  const requestPermission = useCallback(async () => {
    setIsRequestingPermission(true);

    try {
      const result = await manager.requestPermissions();

      if (result.status === 'granted') {
        setHasPermission(true);
        await loadCalendars();
        await loadEventsFromDevice({ forceRefresh: true });
      } else {
        setError(result.error ?? 'Calendar permission was denied');
      }
    } catch {
      setError('Failed to request calendar permission');
    } finally {
      setIsRequestingPermission(false);
    }
  }, [manager, setIsRequestingPermission, setHasPermission, setError, loadCalendars, loadEventsFromDevice]);

  /**
   * Handle calendar selection change
   */
  const handleCalendarSelectionChange = useCallback((newSelection: string[]) => {
    // Update UI immediately (no lag)
    setSelectedCalendarIds(newSelection);

    // Cancel pending reload
    if (calendarSelectionTimeoutRef.current) {
      clearTimeout(calendarSelectionTimeoutRef.current);
    }

    // Debounce event reload (500ms) - allows rapid toggling without lag
    calendarSelectionTimeoutRef.current = setTimeout(async () => {
      await manager.saveCalendarSelection(newSelection);
      await loadEventsFromDevice({ forceRefresh: true });
      calendarSelectionTimeoutRef.current = null;
    }, 500);
  }, [setSelectedCalendarIds, calendarSelectionTimeoutRef, manager, loadEventsFromDevice]);

  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await loadEventsFromDevice({ forceRefresh: true });
    setIsRefreshing(false);
  }, [loadEventsFromDevice, setIsRefreshing]);

  /**
   * Handle updating calendar preference
   */
  const handleUpdatePreference = useCallback(async (
    calendarId: string,
    updates: {
      isVisible?: boolean;
    },
  ) => {
    if (!preferencesManager) {
      console.warn('[calendar] Preferences manager not ready');
      return;
    }

    try {
      await preferencesManager.updateCalendarPreference({
        calendarId,
        ...updates,
      });

      // Reload preferences
      const storage = await preferencesManager.loadPreferences();
      setPreferences(storage.preferences);

      // If visibility changed, update selected calendars
      if (updates.isVisible !== undefined) {
        // Re-filter visible calendars based on updated preferences
        const visibleCalendars = preferencesManager.filterVisibleCalendars(
          deviceCalendars,
          storage.preferences,
        );
        // Reload selection based on newly visible calendars
        const selection = await manager.loadCalendarSelection(visibleCalendars);
        setSelectedCalendarIds(selection);
      }
    } catch (err) {
      console.error('[calendar] Failed to update preference:', err);
    }
  }, [preferencesManager, setPreferences, deviceCalendars, manager, setSelectedCalendarIds]);

  /**
   * Handle FAB press to create new event
   */
  const handleFABPress = useCallback(() => {
    setEventModalMode('create');
    setSelectedEvent(null);
    setShowEventModal(true);
  }, [setEventModalMode, setSelectedEvent, setShowEventModal]);

  /**
   * Handle event press to edit
   */
  const handleEventPress = useCallback(async (event: CalendarEvent) => {
    // Show modal immediately with list data (no attendees yet)
    setSelectedEvent(event);
    setEventModalMode('edit');
    setShowEventModal(true);

    // Enrich with native attendees (lazy-loaded from CalendarContract)
    const nativeParticipants = await AttendeeResolver.resolveForEvent(
      event.id,
      event.organizer,
    );

    // Merge native attendees with existing label participants
    const labelParticipants = event.extendedProperties?.participants
      ?.filter(p => p.role === 'label') ?? [];

    setSelectedEvent({
      ...event,
      extendedProperties: {
        ...event.extendedProperties,
        participants: [...nativeParticipants, ...labelParticipants],
      },
    });
  }, [setSelectedEvent, setEventModalMode, setShowEventModal]);;

  /**
   * Handle saving event (create or update)
   */
  const handleEventSave = useCallback(async (
    input: (CreateCalendarEventInput | UpdateCalendarEventInput) & {
      targetCalendarId?: string;
      preAttachedTasklistIds?: string[];
    },
  ) => {
    setIsSubmittingEvent(true);
    try {
      if (eventModalMode === 'create') {
        const targetCalendarId = (input as any).targetCalendarId ?? selectedCalendarIds[0];
        if (!targetCalendarId) {
          throw new Error('No calendar selected');
        }
        await deviceCalendarService.createEvent(
          targetCalendarId,
          input as CreateCalendarEventInput,
        );
      } else {
        // For recurring events, prompt user for scope
        let recurringOptions: { futureEvents: boolean; instanceStartDate?: Date } | undefined;
        if (selectedEvent?.recurrence) {
          const scope = await RecurringEventUpdateDialog.prompt();
          if (scope === null) {
            setIsSubmittingEvent(false);
            return;
          }
          recurringOptions = {
            futureEvents: scope === 'future',
            instanceStartDate: CalendarEventFormatter.toDate(
              selectedEvent.startDate,
              selectedEvent.startTime,
            ),
          };
        }

        await deviceCalendarService.updateEvent(
          selectedEvent!.id,
          input as UpdateCalendarEventInput,
          recurringOptions,
        );
      }

      // Android CalendarContract needs time to generate recurring event instances
      const hasRecurrence = eventModalMode === 'create'
        ? !!(input as CreateCalendarEventInput).recurrence
        : !!(input as UpdateCalendarEventInput).recurrence || !!selectedEvent?.recurrence;

      if (Platform.OS === 'android' && hasRecurrence) {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Reload events BEFORE dismissing modal (user sees SpinButton spinning)
      await loadEventsFromDevice({ forceRefresh: true });

      // Show toast if event had invited attendees
      const hasInvitedAttendees = (input as CreateCalendarEventInput).extendedProperties?.participants
        ?.some(p => p.role === 'invited') ?? false;

      if (hasInvitedAttendees) {
        Toast.show('Invites sent! Sync will occur in the next few minutes.', {
          type: 'success',
          position: 'top',
          duration: 4000,
        });
      }

      // THEN dismiss modal
      setShowEventModal(false);
    } catch (err) {
      console.error('[calendar] Failed to save event:', err);
      setError((err as Error).message);
    } finally {
      setIsSubmittingEvent(false);
    }
  }, [eventModalMode, selectedCalendarIds, selectedEvent, setIsSubmittingEvent, setShowEventModal, setError, loadEventsFromDevice]);

  /**
   * Handle deleting event
   */
  const handleEventDelete = useCallback(async () => {
    if (!selectedEvent) return;

    try {
      // For recurring events, prompt user for scope
      const hasRecurrence = !!selectedEvent.recurrence;
      let recurringOptions: { futureEvents: boolean; instanceStartDate?: Date } | undefined;
      if (hasRecurrence) {
        const scope = await RecurringEventUpdateDialog.promptDelete();
        if (scope === null) return;
        recurringOptions = {
          futureEvents: scope === 'future',
          instanceStartDate: CalendarEventFormatter.toDate(
            selectedEvent.startDate,
            selectedEvent.startTime,
          ),
        };
      }

      await deviceCalendarService.deleteEvent(selectedEvent.id, recurringOptions);

      // Android CalendarContract needs time to clean up recurring event instances
      if (Platform.OS === 'android' && hasRecurrence) {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Reload events BEFORE dismissing modal
      await loadEventsFromDevice({ forceRefresh: true });

      // THEN dismiss modal
      setShowEventModal(false);
    } catch (err) {
      console.error('[calendar] Failed to delete event:', err);
      setError((err as Error).message);
    }
  }, [selectedEvent, setShowEventModal, setError, loadEventsFromDevice]);

  return {
    autoDetectSelfCalendars,
    handleTodayPress,
    handleDateChanged,
    handleDateSelect,
    loadCalendars,
    checkCalendarPermission,
    requestPermission,
    handleCalendarSelectionChange,
    handleRefresh,
    handleUpdatePreference,
    handleFABPress,
    handleEventPress,
    handleEventSave,
    handleEventDelete,
  };
}
